import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export function useUsers(query: Record<string, string | number> = {}) {
  return useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => apiClient.listUsers(query),
  });
}

export function useAdminAnalytics(months: number) {
  return useQuery({
    queryKey: ['admin', 'analytics', months],
    queryFn: () => apiClient.getAnalytics(months),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => apiClient.getStats(),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      email: string;
      password: string;
      displayName: string;
      role: 'admin' | 'user';
    }) => apiClient.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'user' }) =>
      apiClient.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpdateUserTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, tier }: { userId: string; tier: 'free' | 'pro' }) =>
      apiClient.updateUserTier(userId, tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      apiClient.resetUserPassword(userId, password),
  });
}

export function useBanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => apiClient.banUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// Alias kept for any existing callers
export const useRemoveUser = useBanUser;

export function useUserInventory(userId: string | null, query: Record<string, string | number> = {}) {
  return useQuery({
    queryKey: ['admin', 'userInventory', userId, query],
    queryFn: () => apiClient.listUserInventory(userId!, query),
    enabled: !!userId,
  });
}

export function useAdminOffers(query: Record<string, string | number> = {}) {
  return useQuery({
    queryKey: ['admin', 'offers', query],
    queryFn: () => apiClient.listAllOffers(query),
  });
}

export function useForceWithdrawOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (offerId: string) => apiClient.forceWithdrawOffer(offerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'offers'] });
    },
  });
}

export function useAdminLicenses(query: Record<string, string | number> = {}) {
  return useQuery({
    queryKey: ['admin', 'licenses', query],
    queryFn: () => apiClient.listAdminLicenses(query),
  });
}

export function useLicenseStats() {
  return useQuery({
    queryKey: ['admin', 'licenseStats'],
    queryFn: () => apiClient.getLicenseStats(),
  });
}

export function useIssueLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, notes }: { email: string; notes?: string }) =>
      apiClient.issueLicense(email, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'licenseStats'] });
    },
  });
}

export function useRevokeLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (licenseId: string) => apiClient.revokeLicense(licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'licenseStats'] });
    },
  });
}

export function useDeleteLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (licenseId: string) => apiClient.deleteLicense(licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'licenseStats'] });
    },
  });
}
